'use client';

import { forwardRef, useContext, useId, useRef, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useIsomorphicLayoutEffect } from '../../hooks/use-isomorphic-layout-effect';
import { useMergeRefs } from '../../hooks/use-merge-refs';
import { ModalRootContext } from './context/modal-root.context';
import { modalBodyStyles } from './styles/modal-body.styles';
import type { ModalBodyPropsInterface } from './types/modal-body.types';

/**
 * @since 10.0.0
 */
export const ModalBody = forwardRef<HTMLDivElement, ModalBodyPropsInterface>(
  (props: ModalBodyPropsInterface, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;
    const { modal, scrollDocument } = useContext(ModalRootContext);
    const id = useId();
    const styles = css(modalBodyStyles, cssProp);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const mergedRefs = useMergeRefs([bodyRef, forwardedRef]);

    /*
     * Chrome and Firefox make overflowing scroll containers keyboard focusable
     * on their own, but Safari never does. When the body is the scrollport
     * (`scrollDocument` is false), apply `tabIndex` if content overflows at
     * open. `scrollDocument` scrolls the dialog in the document instead, so
     * the body is not clipped and does not need a tab stop.
     */
    const [isScrollable, setIsScrollable] = useState(false);

    useIsomorphicLayoutEffect(() => {
      if (scrollDocument) {
        setIsScrollable(false);
        return;
      }

      const node = bodyRef.current;
      if (!node) {
        setIsScrollable(false);
        return;
      }

      setIsScrollable(node.scrollHeight > node.clientHeight);
    }, [scrollDocument]);

    useIsomorphicLayoutEffect(() => {
      modal.setDescriptionId(id);
      return () => modal.setDescriptionId(undefined);
    }, [id, modal.setDescriptionId]);

    return (
      <div
        ref={mergedRefs}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Modal.Body"
        id={id}
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={isScrollable ? 0 : undefined}
        className={cx('c11n', 'c11n_modal__body', styles, className)}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

ModalBody.displayName = 'Modal.Body';
